import { CarriagePictogram } from '@/components/icons/CarriagePictogram';
import { useSeatSelection } from '@/hooks/useSeatSelection';
import { cn } from '@/utils/cn';
import {
  Button,
  Icons,
  ScrollArea,
  Typography,
} from '@packages/webshop-design-system';
import { useRef } from 'react';

export const CarriageSelector = () => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const {
    selectedCarriageIndex,
    setSelectedCarriageIndex,
    availabilitiesPlaceMap,
  } = useSeatSelection();
  const carriages = availabilitiesPlaceMap?.placeMaps ?? [];
  const totalCarriages = carriages.length;

  const scrollLeft = (left: number) => {
    scrollRef.current?.scrollBy({ left, behavior: 'smooth' });
  };

  if (totalCarriages <= 1) {
    return null;
  }

  return (
    <div className="flex max-w-[648px] flex-col">
      <div className="flex items-center justify-between gap-2">
        <Button
          onClick={() => scrollLeft(-200)}
          variant="ghost"
          size="small"
          data-testid="carriage-scoll-left-button"
          className="pb-3"
        >
          <Icons.arrowLeft />
        </Button>
        <ScrollArea
          ref={scrollRef}
          orientation="horizontal"
          className="relative"
        >
          <div className="flex pb-3">
            {carriages.map((carriage, index) => {
              const isFirst = index === 0;
              const isLast = index === totalCarriages - 1;
              const hasAvailablePlaces =
                (carriage.availablePlaceCount ?? 0) > 0;
              const Icon = isFirst
                ? Icons.trainNose
                : isLast
                  ? Icons.trainTail
                  : Icons.trainMiddle;

              return (
                <div
                  key={index}
                  className={cn(
                    'relative overflow-y-hidden',
                    hasAvailablePlaces
                      ? 'cursor-pointer'
                      : 'cursor-not-allowed opacity-50',
                    selectedCarriageIndex === index
                      ? 'text-white hover:text-primary-pale'
                      : hasAvailablePlaces
                        ? 'text-neutral hover:text-primary'
                        : 'text-neutral'
                  )}
                  onClick={() =>
                    hasAvailablePlaces && setSelectedCarriageIndex(index)
                  }
                  data-testid={`carriage-button-${index}`}
                >
                  <Icon
                    background={
                      selectedCarriageIndex === index
                        ? 'text-primary'
                        : 'text-white hover:text-primary-pale'
                    }
                  />
                  <div className="pointer-events-none absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center gap-1">
                    <CarriagePictogram width={12} height={12} />
                    <Typography variant="body2" className="font-medium">
                      {carriage.number}
                    </Typography>
                  </div>
                </div>
              );
            })}
          </div>
        </ScrollArea>
        <Button
          onClick={() => scrollLeft(200)}
          variant="ghost"
          size="small"
          data-testid="carriage-scroll-right-button"
          className="pb-3"
        >
          <Icons.arrowRight />
        </Button>
      </div>
      <Icons.arrow
        className={cn('mx-auto', {
          'rotate-180': carriages[0]?.direction?.name === 'IN_DIRECTION',
        })}
      />
    </div>
  );
};
